import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import ConfirmDialog from '../components/ConfirmDialog';
import { accessRightService, NodePermission } from '../services/adminApi';
import { MODULE_TREE, ModuleTreeNode } from '../config/moduleRegistry';

type PermFlags = { canAdd: boolean; canEdit: boolean; canDelete: boolean; canView: boolean };
const EMPTY_FLAGS: PermFlags = { canAdd: false, canEdit: false, canDelete: false, canView: false };

const FLAG_ORDER = ['canView', 'canAdd', 'canEdit', 'canDelete'] as const;
const FLAG_LABEL: Record<(typeof FLAG_ORDER)[number], string> = {
  canView: 'View',
  canAdd: 'Add',
  canEdit: 'Edit',
  canDelete: 'Delete',
};

const collectKeys = (nodes: ModuleTreeNode[]): string[] => {
  const keys: string[] = [];
  const walk = (n: ModuleTreeNode) => {
    keys.push(n.key);
    (n.children || []).forEach(walk);
  };
  nodes.forEach(walk);
  return keys;
};

export default function AccessRightEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [notFound, setNotFound] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingTree, setSavingTree] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(false);
  const [confirmTree, setConfirmTree] = useState(false);
  const [message, setMessage] = useState('');

  const [perms, setPerms] = useState<Record<string, PermFlags>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    // Modules start expanded (their tabs are the primary thing to configure);
    // sub-tabs start collapsed since they're the bulk of the 167 rows.
    const initial: Record<string, boolean> = {};
    MODULE_TREE.forEach((m) => {
      initial[m.key] = true;
    });
    return initial;
  });

  useEffect(() => {
    if (isNew) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const [rightsRes, nodeRes] = await Promise.all([
        accessRightService.list(),
        accessRightService.getNodePermissions(id as string),
      ]);
      const right = (rightsRes.data.accessRights || []).find((r: any) => r.id === id);
      if (!right) {
        setNotFound(true);
        return;
      }
      setCode(right.code);
      setDescription(right.description || '');

      const baseFlags: PermFlags = {
        canAdd: right.can_add,
        canEdit: right.can_edit,
        canDelete: right.can_delete,
        canView: right.can_view,
      };
      const overrides: Record<string, PermFlags> = {};
      (nodeRes.data.nodePermissions || []).forEach((row: any) => {
        overrides[row.node_key] = {
          canAdd: row.can_add,
          canEdit: row.can_edit,
          canDelete: row.can_delete,
          canView: row.can_view,
        };
      });

      // Every node defaults to the access right's base flags unless it already
      // has its own saved override — this mirrors how the backend actually
      // resolves permissions today (permissions.ts's resolveNodePermission
      // falls back to the base columns when no node override exists), so the
      // tree shows real current behavior before anything is saved here.
      const initial: Record<string, PermFlags> = {};
      collectKeys(MODULE_TREE).forEach((key) => {
        initial[key] = overrides[key] || baseFlags;
      });
      setPerms(initial);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setConfirmDetails(true);
  };

  const performCreateDetails = async () => {
    setSavingDetails(true);
    try {
      const res = await accessRightService.create({
        code,
        description,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canView: false,
      });
      setConfirmDetails(false);
      navigate(`/admin/access-rights/${res.data.accessRight.id}`);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create access right');
    } finally {
      setSavingDetails(false);
    }
  };

  const setNodeFlag = (nodeKey: string, flag: keyof PermFlags, value: boolean) => {
    setPerms((prev) => ({ ...prev, [nodeKey]: { ...(prev[nodeKey] || EMPTY_FLAGS), [flag]: value } }));
  };

  const toggleExpanded = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const performSaveTree = async () => {
    setSavingTree(true);
    try {
      const payload: NodePermission[] = collectKeys(MODULE_TREE).map((key) => ({
        nodeKey: key,
        ...(perms[key] || EMPTY_FLAGS),
      }));
      await accessRightService.updateNodePermissions(id as string, payload);
      setConfirmTree(false);
      setMessage('Permissions saved');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSavingTree(false);
    }
  };

  const renderNode = (node: ModuleTreeNode, depth: number) => {
    const hasChildren = !!node.children?.length;
    const isOpen = expanded[node.key];
    const flags = perms[node.key] || EMPTY_FLAGS;
    return (
      <div key={node.key}>
        <div
          className="flex items-center gap-3 py-2 border-b border-gray-100 hover:bg-gray-50"
          style={{ paddingLeft: `${depth * 20}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleExpanded(node.key)}
              className="w-5 text-gray-400 hover:text-gray-700"
              aria-label={isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
            >
              {isOpen ? '▾' : '▸'}
            </button>
          ) : (
            <span className="w-5" />
          )}
          <span className={`flex-1 text-sm ${depth === 0 ? 'font-semibold' : ''}`}>{node.label}</span>
          {FLAG_ORDER.map((flag) => (
            <label key={flag} className="flex items-center gap-1 text-xs text-gray-600 w-16">
              <input
                type="checkbox"
                checked={flags[flag]}
                onChange={(e) => setNodeFlag(node.key, flag, e.target.checked)}
              />
              {FLAG_LABEL[flag]}
            </label>
          ))}
        </div>
        {hasChildren && isOpen && node.children!.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  if (isNew) {
    return (
      <div className="p-6 max-w-lg">
        <Link to="/admin/access-rights" className="text-sm text-blue-600 hover:underline">
          ← Back to Access Rights
        </Link>
        <h1 className="text-2xl font-bold mt-1 mb-6">Add Access Right</h1>
        <form onSubmit={handleCreateDetailsSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
          <Input
            label="Access Right Code (max 15 chars)"
            maxLength={15}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
          />
          <Input
            label="Description (max 50 chars)"
            maxLength={50}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className="text-sm text-gray-500">
            Save the code and description first — you'll configure per-tab permissions on the next screen.
          </p>
          <Button type="submit" className="w-full">
            Create &amp; Continue
          </Button>
        </form>

        <ConfirmDialog
          isOpen={confirmDetails}
          title="Create Access Right"
          message="Are you sure you want to create this access right?"
          confirmLabel="Create"
          variant="primary"
          loading={savingDetails}
          onConfirm={performCreateDetails}
          onCancel={() => setConfirmDetails(false)}
        />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6">
        <Link to="/admin/access-rights" className="text-sm text-blue-600 hover:underline">
          ← Back to Access Rights
        </Link>
        <p className="mt-4 text-gray-500">Access right not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Link to="/admin/access-rights" className="text-sm text-blue-600 hover:underline">
        ← Back to Access Rights
      </Link>
      <h1 className="text-2xl font-bold mt-1 mb-6">
        {loading ? 'Access Right' : `${code}${description ? ` — ${description}` : ''}`}
      </h1>

      {message && (
        <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500 mb-3">
            Set View/Add/Edit/Delete per module, tab, and sub-tab. A tab or sub-tab with no box checked has no
            access, regardless of its parent's setting.
          </p>
          <div className="flex items-center gap-3 py-2 border-b-2 border-gray-200 font-semibold text-xs text-gray-500 uppercase">
            <span className="w-5" />
            <span className="flex-1">Module / Tab / Sub-tab</span>
            {FLAG_ORDER.map((flag) => (
              <span key={flag} className="w-16">
                {FLAG_LABEL[flag]}
              </span>
            ))}
          </div>
          {MODULE_TREE.map((mod) => renderNode(mod, 0))}
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setConfirmTree(true)} loading={savingTree}>
              Save Permissions
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmTree}
        title="Save Permissions"
        message="Are you sure you want to save these tab/sub-tab permissions?"
        confirmLabel="Save"
        variant="primary"
        loading={savingTree}
        onConfirm={performSaveTree}
        onCancel={() => setConfirmTree(false)}
      />
    </div>
  );
}
